// -----------------------------------------------------------------------------
// ui/app.ts — the editor shell.
//
// Constructs the engine services (store, media manager, preview compositor,
// player, exporter, scratch) and the ephemeral UI signals, bundles them into one
// EditorContext, then builds the Clipchamp-style layout: top bar, a left rail +
// panel column, the preview stage, and the bottom timeline. It also owns the
// app-level glue the panels don't: keyboard shortcuts, undo/redo buttons, save /
// open, and the export flow. The engine runs on its own clock — this file only
// sends it commands and reflects its state. (CLAUDE.md §4.)
// -----------------------------------------------------------------------------
import { RemoveClipCommand, SplitClipCommand } from '../core/commands/clip-commands';
import {
  type MediaAsset,
  type Project,
  clipEnd,
  createDefaultProject,
  locateClip,
  projectDuration,
} from '../core/model';
import { ProjectStore } from '../core/store';
import { Compositor } from '../engine/compositor/gl';
import { chooseExportPath } from '../engine/export/export-path';
import { type ExportProgress, Exporter } from '../engine/export/exporter';
import { MediaManager } from '../engine/media/source';
import { Player } from '../engine/player';
import type { RuntimeConfig } from '../preflight/capabilities';
import type { Capabilities } from '../preflight/detect';
import { buildCapabilityBanner } from '../preflight/gate-screen';
import type { SavedDocument } from '../core/serialize';
import { loadProjectFromFile, saveProject } from '../storage/projects';
import { ScratchStore } from '../storage/scratch';
import type { EditorContext, ExportUiState, StatusMessage } from './context';
import { clear, el } from './dom';
import { createMediaPanel } from './panels/media';
import { createPropertyPanel } from './panels/property';
import { createTextPanel } from './panels/text';
import { createStage } from './preview/stage';
import { signal } from './reactive';
import { createTimelineControls } from './timeline/controls';
import { attachTimelineInteractions } from './timeline/interactions';
import { TimelineView } from './timeline/timeline';

export function mountEditor(root: HTMLElement, caps: Capabilities, config: RuntimeConfig): void {
  // ---- services --------------------------------------------------------------
  const project = createDefaultProject();
  const store = new ProjectStore(project);
  const media = new MediaManager();
  const scratch = new ScratchStore(config.useOpfs);

  const previewCanvas = document.createElement('canvas');
  const [renderW, renderH] = previewSize(project, config);
  const compositor = new Compositor(previewCanvas, renderW, renderH);

  const player = new Player(store, media, compositor);
  const exporter = new Exporter(store, media);

  // Resize the preview compositor when the project's canvas size changes (Size
  // presets / project load). Registered before the stage so it runs first, then
  // the stage's fit logic re-letterboxes against the new aspect.
  let lastDims = `${project.width}x${project.height}`;
  store.subscribe(() => {
    const p = store.state.project;
    const dims = `${p.width}x${p.height}`;
    if (dims === lastDims) return;
    lastDims = dims;
    const [w, h] = previewSize(p, config);
    compositor.resize(w, h);
    player.requestRender();
  });

  const signals = {
    pxPerSecond: signal(80),
    status: signal<StatusMessage>({ text: '', kind: 'info' }),
    exportState: signal<ExportUiState>({ active: false, phase: '', frac: 0 }),
  };

  const ctx: EditorContext = {
    store,
    media,
    player,
    exporter,
    scratch,
    caps,
    config,
    signals,
    setStatus(text, kind = 'info') {
      signals.status.set({ text, kind });
    },
  };

  // ---- top bar ---------------------------------------------------------------
  const projectName = el('input', { class: 'zf-project-name', value: project.name, 'aria-label': 'Project name', title: 'Project name' }) as HTMLInputElement;
  projectName.addEventListener('change', () => {
    store.state.project.name = projectName.value.trim() || 'Untitled project';
    store.notify();
  });

  const undoBtn = el('button', { class: 'zf-btn zf-btn-ghost', title: 'Undo (Ctrl+Z)', 'aria-label': 'Undo', onclick: () => store.undo() }, '↶');
  const redoBtn = el('button', { class: 'zf-btn zf-btn-ghost', title: 'Redo (Ctrl+Shift+Z)', 'aria-label': 'Redo', onclick: () => store.redo() }, '↷');
  const newBtn = el('button', { class: 'zf-btn', onclick: onNewProject }, 'New');
  const openBtn = el('button', { class: 'zf-btn', onclick: onOpen }, 'Open');
  const saveBtn = el('button', { class: 'zf-btn', onclick: onSave }, 'Save');
  const exportBtn = el('button', { class: 'zf-btn zf-btn-primary', onclick: onExport }, '⬇ Export');

  const bannerSlot = el('div', {});
  const banner = buildCapabilityBanner(caps);
  if (banner) bannerSlot.append(banner);

  const topbar = el(
    'div',
    { class: 'zf-topbar' },
    el('div', { class: 'zf-brand' }, 'Zero', el('span', {}, 'Frame')),
    projectName,
    el('span', { class: 'zf-topbar-spacer' }),
    bannerSlot,
    undoBtn,
    redoBtn,
    newBtn,
    openBtn,
    saveBtn,
    exportBtn,
  );

  function refreshTopbar(): void {
    undoBtn.toggleAttribute('disabled', !store.canUndo());
    redoBtn.toggleAttribute('disabled', !store.canRedo());
  }
  store.subscribe(refreshTopbar);
  refreshTopbar();

  // Keep the browser tab title in sync with the project name.
  const syncTitle = (): void => {
    const name = store.state.project.name.trim();
    document.title =
      name && name !== 'Untitled project' ? `ZeroFrame — ${name}` : 'ZeroFrame — browser video editor';
  };
  store.subscribe(syncTitle);
  syncTitle();

  // ---- left rail + panels ----------------------------------------------------
  const mediaPanel = createMediaPanel(ctx);
  const textPanel = createTextPanel(ctx);
  const propertyPanel = createPropertyPanel(ctx);
  const panelHost = el('div', { class: 'zf-panel-host' }, mediaPanel, textPanel, propertyPanel);

  const railMedia = railButton('🎬', 'Media', () => showPanel('media'));
  const railText = railButton('T', 'Text', () => showPanel('text'));
  const railAdjust = railButton('🎨', 'Adjust', () => showPanel('adjust'));
  const rail = el('div', { class: 'zf-rail' }, railMedia, railText, railAdjust);

  type PanelName = 'media' | 'text' | 'adjust';
  function showPanel(name: PanelName): void {
    mediaPanel.style.display = name === 'media' ? '' : 'none';
    textPanel.style.display = name === 'text' ? '' : 'none';
    propertyPanel.style.display = name === 'adjust' ? '' : 'none';
    railMedia.classList.toggle('active', name === 'media');
    railText.classList.toggle('active', name === 'text');
    railAdjust.classList.toggle('active', name === 'adjust');
  }

  const stage = createStage(ctx, previewCanvas);
  const main = el('div', { class: 'zf-main' }, rail, panelHost, stage);

  // ---- timeline --------------------------------------------------------------
  const view = new TimelineView(ctx);
  attachTimelineInteractions(view, ctx);
  const timelineControls = createTimelineControls(ctx);
  const timelineWrap = el('div', { class: 'zf-timeline-canvas-wrap' }, view.canvas);
  const timeline = el('div', { class: 'zf-timeline' }, timelineControls, timelineWrap);

  const redrawTimeline = (): void => view.draw();
  store.subscribe(redrawTimeline);
  player.events.on('time', redrawTimeline);
  signals.pxPerSecond.subscribe(redrawTimeline);
  window.addEventListener('resize', redrawTimeline);

  // Preview renders on demand now (no idle rAF loop), so repaint it whenever the
  // document changes (add/trim/split/move/text edits/load).
  store.subscribe(() => player.requestRender());

  // ---- status + export overlay ----------------------------------------------
  const statusEl = el('div', { class: 'zf-status' });
  let statusTimer = 0;
  signals.status.subscribe(({ text, kind }) => {
    if (!text) {
      statusEl.classList.remove('show');
      return;
    }
    statusEl.textContent = text;
    statusEl.className = 'zf-status show' + (kind === 'error' ? ' error' : '');
    clearTimeout(statusTimer);
    statusTimer = window.setTimeout(() => statusEl.classList.remove('show'), kind === 'error' ? 6000 : 3000);
  });

  const progressBar = el('div', { class: 'zf-progress-bar' });
  const exportMessage = el('div', {});
  const cancelExportBtn = el(
    'button',
    { class: 'zf-btn', onclick: () => exporter.cancel() },
    'Cancel',
  );
  const exportModal = el(
    'div',
    { class: 'zf-modal', style: 'display:none' },
    el('div', { class: 'zf-modal-card' },
      el('h3', {}, 'Exporting…'),
      exportMessage,
      el('div', { class: 'zf-progress' }, progressBar),
      el('p', { class: 'zf-panel-hint' }, 'Rendering on your device. Keep this tab open.'),
      cancelExportBtn,
    ),
  );
  signals.exportState.subscribe((s) => {
    exportModal.style.display = s.active ? '' : 'none';
    progressBar.style.width = Math.round(s.frac * 100) + '%';
    exportMessage.textContent = s.phase;
  });

  exporter.events.on('progress', (p: ExportProgress) => {
    const frac = p.totalFrames ? p.frame / p.totalFrames : 0;
    const phase =
      p.phase === 'render'
        ? `Rendering frame ${p.frame} / ${p.totalFrames}`
        : p.phase === 'finalize'
          ? 'Finalising file…'
          : p.phase;
    signals.exportState.set({ active: true, phase, frac });
  });

  // ---- assemble + boot -------------------------------------------------------
  clear(root);
  root.className = '';
  root.append(el('div', { class: 'zf-app' }, topbar, main, timeline), statusEl, exportModal);
  showPanel('media');
  attachKeyboard();
  player.mount();
  requestAnimationFrame(redrawTimeline);
  ctx.setStatus('Import a video or image to begin.');

  // ---- handlers --------------------------------------------------------------
  function onNewProject(): void {
    const hasContent = store.state.assets.length > 0 || projectDuration(store.state.project) > 0;
    if (hasContent && !window.confirm('Discard the current project and start a new one?')) return;
    media.clearSources();
    const fresh = createDefaultProject();
    store.load(fresh, []);
    const [w, h] = previewSize(fresh, config);
    compositor.resize(w, h);
    projectName.value = fresh.name;
    player.seek(0);
    ctx.setStatus('New project created.');
  }

  async function onSave(): Promise<void> {
    try {
      await saveProject(store.state.project, store.state.assets, scratch, true);
      ctx.setStatus('Project saved (downloaded + kept in this browser).');
    } catch (err) {
      ctx.setStatus('Save failed: ' + errText(err), 'error');
    }
  }

  async function onOpen(): Promise<void> {
    try {
      const doc = await loadProjectFromFile();
      if (doc) await loadDocument(doc);
    } catch (err) {
      ctx.setStatus('Open failed: ' + errText(err), 'error');
    }
  }

  async function loadDocument(doc: SavedDocument): Promise<void> {
    for (const asset of doc.assets) {
      if (!asset.scratchKey) continue;
      const blob = await scratch.get(asset.scratchKey);
      if (blob) {
        try {
          await media.reopen(asset, blob);
        } catch {
          ctx.setStatus(`Couldn’t decode media “${asset.name}”.`, 'error');
        }
      } else {
        ctx.setStatus(`Media “${asset.name}” isn’t in this browser’s storage.`, 'error');
      }
    }
    store.load(doc.project, doc.assets as MediaAsset[]);
    const [w, h] = previewSize(doc.project, config);
    compositor.resize(w, h);
    projectName.value = doc.project.name;
    player.seek(0);
    ctx.setStatus('Project loaded.');
  }

  async function onExport(): Promise<void> {
    if (projectDuration(store.state.project) <= 0) {
      ctx.setStatus('Add a clip to the timeline before exporting.', 'error');
      return;
    }
    player.pause();
    // Stop the live preview loop so its main-thread WebGL + decode requests don't
    // contend with the export compositor; resume it when we're done.
    player.unmount();
    const plan = chooseExportPath(store.state.project, config);
    signals.exportState.set({ active: true, phase: 'Preparing encoder…', frac: 0 });
    try {
      const blob = await exporter.export(plan);
      downloadBlob(blob, plan.fileName);
      ctx.setStatus(`Exported ${plan.fileName} — ${(blob.size / 1e6).toFixed(1)} MB.`);
    } catch (err) {
      const message = errText(err);
      if (/cancel/i.test(message)) ctx.setStatus('Export cancelled.');
      else ctx.setStatus('Export failed: ' + message, 'error');
    } finally {
      signals.exportState.set({ active: false, phase: '', frac: 0 });
      player.mount();
    }
  }

  function attachKeyboard(): void {
    window.addEventListener('keydown', (e) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      // In a text field: let it handle everything.
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;

      // Modifier shortcuts work regardless of which control is focused.
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        e.shiftKey ? store.redo() : store.undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        store.redo();
        return;
      }
      if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault(); // don't trigger the browser's "save page"
        void onSave();
        return;
      }

      // Un-modified keys: defer to a focused button-like control so its own
      // Space/Enter activation isn't hijacked by the global shortcuts.
      if (tag === 'BUTTON' || tag === 'A' || tag === 'SELECT' || target?.getAttribute('role') === 'button') {
        return;
      }

      const fps = store.state.project.fps;
      if (e.code === 'Space') {
        e.preventDefault();
        player.toggle();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        const id = store.state.selectedClipId;
        if (id) {
          e.preventDefault();
          store.dispatch(new RemoveClipCommand(id));
          store.select(null);
        }
      } else if (e.key === 'ArrowLeft') {
        player.seek(player.getTime() - 1 / fps);
      } else if (e.key === 'ArrowRight') {
        player.seek(player.getTime() + 1 / fps);
      } else if (e.key === 'Home') {
        e.preventDefault();
        player.seek(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        player.seek(projectDuration(store.state.project));
      } else if (e.key.toLowerCase() === 's') {
        // Split the selected clip at the playhead (only if the playhead is inside it).
        const id = store.state.selectedClipId;
        if (id) {
          const loc = locateClip(store.state.project, id);
          if (loc) {
            const t = player.getTime();
            if (t > loc.clip.startSec && t < clipEnd(loc.clip)) {
              e.preventDefault();
              store.dispatch(new SplitClipCommand(id, t));
              ctx.setStatus('Clip split.');
            }
          }
        }
      }
    });
  }
}

// ---- helpers -----------------------------------------------------------------

function railButton(icon: string, label: string, onClick: () => void): HTMLButtonElement {
  return el(
    'button',
    { class: 'zf-rail-btn', title: label, onclick: onClick },
    el('span', {}, icon),
    el('small', {}, label),
  );
}

function previewSize(project: Project, config: RuntimeConfig): [number, number] {
  const w = Math.max(2, Math.min(Math.round(project.width * config.previewScale), config.previewMaxWidth));
  const h = Math.max(2, Math.round((w * project.height) / project.width));
  return [w, h];
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
